import { useCallback, useMemo, type ChangeEvent } from 'react'
import { useIntl, type IntlShape } from 'react-intl'

import { usePermission } from '@/shared/access'
import { useLocaleSettings } from '@/shared/i18n/I18nProvider'
import { DatePicker, Select, TextField, type SelectOption } from '@/shared/ui'

import { labelText, operatorsFor, takesNumber, type FilterSuggestions, type SearchFilterField } from './definition'
import { FilterChipGroup } from './FilterChipGroup'
import { FilterSection } from './FilterSection'
import { usePickerLabels } from './labels'
import { SuggestField } from './SuggestField'
import {
  periodLimits,
  periodUnits,
  takesNoValue,
  type FieldFilter,
  type FilterOperator,
  type PeriodUnit,
} from './search-state'

/** A field's filter as the panel edits it, complete or not. */
export interface DraftFilter {
  readonly operator: FilterOperator
  readonly values: readonly string[]
}

/** What a field's filter starts as: its default operator and no value. */
export function emptyDraft(field: SearchFilterField): DraftFilter {
  const operator = operatorsFor(field)[0] ?? 'contains'
  return { operator, values: [] }
}

/** The draft for a filter already applied. */
export function draftOf(filter: FieldFilter): DraftFilter {
  return { operator: filter.operator, values: filter.values }
}

/** A choice field's conditions as the mockups' Is / Is not chips. */
const choiceConditionIds: Readonly<Partial<Record<FilterOperator, string>>> = {
  in: 'search.filters.is',
  notIn: 'search.filters.isNot',
  isNull: 'search.filters.isEmpty',
  isNotNull: 'search.filters.isNotEmpty',
}

/**
 * One field in the filter sidebar, as a collapsible group (`FilterSection`) named by the field:
 * its condition and the value that condition takes. A choice field shows both as the mockups'
 * chips (`FilterChoiceField`, `FilterChipGroup`): Is or Is not, then its options. Any other keeps
 * a condition select, then text, a number, a date range or a relative period.
 */
export function FilterFieldEditor(props: {
  readonly field: SearchFilterField
  readonly draft: DraftFilter
  readonly onChange: (field: SearchFilterField, draft: DraftFilter) => void
  /** The time zone the search reads days in, so the calendar's today matches it. */
  readonly timeZone: string
  /** How many values the applied filter has chosen, shown beside the field's name. */
  readonly count: number
  /** The search shows inactive records, so a field's suggestions include them. */
  readonly includeInactive?: boolean
}) {
  const intl = useIntl()
  const { field, draft, onChange } = props
  const label = labelText(intl, field)
  const isChoice = field.kind === 'choice'

  const operatorOptions = useMemo<readonly SelectOption<FilterOperator>[]>(
    () =>
      operatorsFor(field).map((operator) => ({
        value: operator,
        label: intl.formatMessage({ id: (isChoice ? choiceConditionIds[operator] : undefined) ?? `search.operator.${operator}` }),
      })),
    [field, intl, isChoice],
  )

  const setOperator = useCallback(
    (operator: FilterOperator) => {
      // A value carries over only to an operator that takes the same shape of value.
      const keep = shapeOf(field, operator) === shapeOf(field, draft.operator)
      onChange(field, { operator, values: keep ? draft.values : [] })
    },
    [field, draft, onChange],
  )
  const setChosenOperator = useCallback(
    (chosen: readonly string[]) => {
      const operator = operatorOptions.find((option) => option.value === chosen[0])?.value
      if (operator !== undefined) {
        setOperator(operator)
      }
    },
    [operatorOptions, setOperator],
  )
  const setValues = useCallback(
    (values: readonly string[]) => onChange(field, { operator: draft.operator, values }),
    [field, draft.operator, onChange],
  )
  const conditionLabel = intl.formatMessage({ id: 'search.filters.condition' }, { field: label })

  return (
    <FilterSection label={label} count={props.count}>
      {operatorOptions.length > 1 &&
        (isChoice ? (
          <FilterChipGroup label={conditionLabel} options={operatorOptions} chosen={[draft.operator]} onChange={setChosenOperator} />
        ) : (
          <Select label={conditionLabel} hideLabel value={draft.operator} onChange={setOperator} options={operatorOptions} className="w-full" />
        ))}
      <ValueEditor field={field} label={label} draft={draft} onValues={setValues} timeZone={props.timeZone} includeInactive={props.includeInactive === true} />
    </FilterSection>
  )
}

type Shape = 'none' | 'one' | 'list' | 'range' | 'period'

function shapeOf(field: SearchFilterField, operator: FilterOperator): Shape {
  if (takesNoValue(operator)) {
    return 'none'
  }
  switch (operator) {
    case 'in':
    case 'notIn':
      return 'list'
    case 'between':
    case 'notBetween':
      return 'range'
    case 'within':
    case 'notWithin':
      return 'period'
    default:
      return field.kind === 'choice' ? 'list' : 'one'
  }
}

function ValueEditor(props: {
  readonly field: SearchFilterField
  readonly label: string
  readonly draft: DraftFilter
  readonly onValues: (values: readonly string[]) => void
  readonly timeZone: string
  readonly includeInactive: boolean
}) {
  const { field, label, draft, onValues } = props
  switch (shapeOf(field, draft.operator)) {
    case 'none':
      return null
    case 'list':
      return <ChoiceValues field={field} label={label} values={draft.values} onValues={onValues} />
    case 'range':
      return field.kind === 'date' ? (
        <DateRange label={label} values={draft.values} onValues={onValues} timeZone={props.timeZone} />
      ) : (
        <NumberRange label={label} integer={field.integer === true} values={draft.values} onValues={onValues} />
      )
    case 'period':
      return <PeriodValues label={label} values={draft.values} onValues={onValues} />
    case 'one':
      return field.suggest !== undefined ? (
        <SuggestedValue
          label={label}
          source={field.suggest}
          value={draft.values[0] ?? ''}
          onValues={onValues}
          includeInactive={props.includeInactive}
        />
      ) : (
        <OneValue
          label={label}
          numeric={field.kind === 'number'}
          integer={field.integer === true}
          value={draft.values[0] ?? ''}
          onValues={onValues}
        />
      )
  }
}

function OneValue(props: {
  readonly label: string
  readonly numeric: boolean
  readonly integer: boolean
  readonly value: string
  readonly onValues: (values: readonly string[]) => void
}) {
  const intl = useIntl()
  const { onValues } = props
  const change = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => onValues([event.target.value]),
    [onValues],
  )

  return (
    <TextField
      label={intl.formatMessage({ id: 'search.filters.value' }, { field: props.label })}
      hideLabel
      value={props.value}
      onChange={change}
      inputMode={props.numeric ? (props.integer ? 'numeric' : 'decimal') : undefined}
      placeholder={intl.formatMessage({ id: 'search.filters.placeholder' }, { field: props.label })}
      {...numberError(intl, props.numeric, props.integer, props.value)}
    />
  )
}

/** A text value with suggestions, for a viewer who may read where they come from; else a plain one. */
function SuggestedValue(props: {
  readonly label: string
  readonly source: FilterSuggestions
  readonly value: string
  readonly onValues: (values: readonly string[]) => void
  readonly includeInactive: boolean
}) {
  const intl = useIntl()
  const { onValues } = props
  const mayRead = usePermission(props.source.permission)
  const change = useCallback((value: string) => onValues([value]), [onValues])
  if (!mayRead) {
    return <OneValue label={props.label} numeric={false} integer={false} value={props.value} onValues={onValues} />
  }
  return (
    <SuggestField
      label={intl.formatMessage({ id: 'search.filters.value' }, { field: props.label })}
      fieldLabel={props.label}
      placeholder={intl.formatMessage({ id: 'search.filters.placeholder' }, { field: props.label })}
      value={props.value}
      onChange={change}
      source={props.source}
      includeInactive={props.includeInactive}
    />
  )
}

/**
 * The field's error when it should hold a number, or a whole number, and holds other text; it is
 * not applied till fixed.
 */
function numberError(intl: IntlShape, numeric: boolean, integer: boolean, value: string): { readonly error?: string } {
  return numeric && value.trim() !== '' && !takesNumber({ integer }, value)
    ? { error: intl.formatMessage({ id: integer ? 'search.filters.notWholeNumber' : 'search.filters.notNumber' }) }
    : {}
}

/** The amount's error when it is not a whole number from 1 to the unit's limit; not applied till fixed. */
function amountError(intl: IntlShape, amount: string, unit: PeriodUnit): { readonly error?: string } {
  const max = periodLimits[unit]
  return amount !== '' && !(/^[1-9]\d{0,3}$/u.test(amount) && Number(amount) <= max)
    ? { error: intl.formatMessage({ id: 'search.filters.badAmount' }, { max }) }
    : {}
}

/** A choice field's options as chips, any number of which may be chosen. */
function ChoiceValues(props: {
  readonly field: SearchFilterField
  readonly label: string
  readonly values: readonly string[]
  readonly onValues: (values: readonly string[]) => void
}) {
  const intl = useIntl()
  const options = useMemo(
    () => (props.field.options ?? []).map((option) => ({ value: option.value, label: labelText(intl, option) })),
    [props.field.options, intl],
  )
  return <FilterChipGroup label={props.label} options={options} chosen={props.values} onChange={props.onValues} isMultiple />
}

function NumberRange(props: {
  readonly label: string
  readonly integer: boolean
  readonly values: readonly string[]
  readonly onValues: (values: readonly string[]) => void
}) {
  const intl = useIntl()
  const { values, onValues } = props
  const from = values[0] ?? ''
  const to = values[1] ?? ''
  const changeFrom = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => onValues([event.target.value, to]),
    [to, onValues],
  )
  const changeTo = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => onValues([from, event.target.value]),
    [from, onValues],
  )
  const fromLabel = intl.formatMessage({ id: 'search.filters.from' })
  const toLabel = intl.formatMessage({ id: 'search.filters.to' })

  return (
    <div className="flex gap-2">
      <TextField
        label={`${props.label}: ${fromLabel}`}
        hideLabel
        inputMode={props.integer ? 'numeric' : 'decimal'}
        placeholder={fromLabel}
        value={from}
        onChange={changeFrom}
        {...numberError(intl, true, props.integer, from)}
        className="min-w-0 flex-1"
      />
      <TextField
        label={`${props.label}: ${toLabel}`}
        hideLabel
        inputMode={props.integer ? 'numeric' : 'decimal'}
        placeholder={toLabel}
        value={to}
        onChange={changeTo}
        {...numberError(intl, true, props.integer, to)}
        className="min-w-0 flex-1"
      />
    </div>
  )
}

function DateRange(props: {
  readonly label: string
  readonly values: readonly string[]
  readonly onValues: (values: readonly string[]) => void
  readonly timeZone: string
}) {
  const intl = useIntl()
  const { locale } = useLocaleSettings()
  const { timeZone } = props
  const { values, onValues } = props
  const from = values[0] ?? ''
  const to = values[1] ?? ''
  const changeFrom = useCallback((day: string | null) => onValues([day ?? '', to]), [to, onValues])
  const changeTo = useCallback((day: string | null) => onValues([from, day ?? '']), [from, onValues])
  const fromLabel = intl.formatMessage({ id: 'search.filters.from' })
  const toLabel = intl.formatMessage({ id: 'search.filters.to' })
  const fromPicker = usePickerLabels(`${props.label}: ${fromLabel}`)
  const toPicker = usePickerLabels(`${props.label}: ${toLabel}`)

  // One above the other, as the sidebar is too narrow for two calendars' fields side by side.
  return (
    <div className="flex flex-col gap-2">
      <DatePicker
        value={from}
        onChange={changeFrom}
        placeholder={`${props.label}: ${fromLabel}`}
        clearable
        labels={fromPicker}
        locale={locale}
        timeZone={timeZone}
        className="w-full"
      />
      <DatePicker
        value={to}
        onChange={changeTo}
        placeholder={`${props.label}: ${toLabel}`}
        clearable
        labels={toPicker}
        locale={locale}
        timeZone={timeZone}
        className="w-full"
      />
    </div>
  )
}

function PeriodValues(props: {
  readonly label: string
  readonly values: readonly string[]
  readonly onValues: (values: readonly string[]) => void
}) {
  const intl = useIntl()
  const { values, onValues } = props
  const direction = values[0] === 'next' ? 'next' : 'last'
  const amount = values[1] ?? ''
  const unit: PeriodUnit = periodUnits.includes(values[2] as PeriodUnit) ? (values[2] as PeriodUnit) : 'days'
  const count = Number(amount) || 2

  const directions = useMemo<readonly SelectOption<string>[]>(
    () => [
      { value: 'last', label: intl.formatMessage({ id: 'search.period.last' }) },
      { value: 'next', label: intl.formatMessage({ id: 'search.period.next' }) },
    ],
    [intl],
  )
  const units = useMemo<readonly SelectOption<string>[]>(
    () =>
      periodUnits.map((value) => ({
        value,
        label: intl.formatMessage({ id: `search.unit.${value}` }, { count }),
      })),
    [intl, count],
  )

  const setDirection = useCallback((next: string) => onValues([next, amount, unit]), [amount, unit, onValues])
  const setAmount = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => onValues([direction, event.target.value, unit]),
    [direction, unit, onValues],
  )
  const setUnit = useCallback((next: string) => onValues([direction, amount, next]), [direction, amount, onValues])

  return (
    <div className="flex flex-wrap gap-2">
      <Select
        label={intl.formatMessage({ id: 'search.filters.direction' }, { field: props.label })}
        hideLabel
        value={direction}
        onChange={setDirection}
        options={directions}
        className="min-w-0 flex-1"
      />
      <TextField
        label={intl.formatMessage({ id: 'search.filters.amount' }, { field: props.label })}
        hideLabel
        inputMode="numeric"
        value={amount}
        onChange={setAmount}
        className="w-20 shrink-0"
        {...amountError(intl, amount, unit)}
      />
      <Select
        label={intl.formatMessage({ id: 'search.filters.unit' }, { field: props.label })}
        hideLabel
        value={unit}
        onChange={setUnit}
        options={units}
        className="min-w-0 flex-1"
      />
    </div>
  )
}
